import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';

import {
  login,
  loginAsGuest,
  me as loadMe,
  samlDiscovery,
  samlRedirect,
} from 'js/actions/account-actions';

import LoginPageComponent from '../components/LoginPageComponent';
import { storePasswordCredential } from 'js/utils/credentials';
import { selectIsAuthenticated } from 'js/store/reducers/identity';
import { debounce } from 'lodash';

// how long to wait on the stored session before showing the form anyway
const SESSION_CHECK_TIMEOUT_MS = 5000;

export function LoginPageContainer({
  currentWorkspaceId,
  history,
  isAuthenticated,
  location,
  login,
  loginAsGuest,
  loadMe,
  samlDiscovery = () => {},
  samlRedirect = () => {},
}) {
  const fromUrl = useMemo(() => {
    const { from = null } = location.state ?? {};

    if (from) {
      return from.pathname + (from.search ?? '');
    }

    return null;
  }, [location.state]);

  const redirectAfterLogin = useCallback(
    (me) => {
      let url;

      if (me.workspaces.length > 0) {
        url =
          fromUrl != null
            ? fromUrl
            : `/w/${currentWorkspaceId || me.workspaces[0].id}/dashboard`;
      } else {
        url = fromUrl != null ? fromUrl : `/a/${me.accounts[0].id}`;
      }

      history.push(url);
    },
    [currentWorkspaceId, fromUrl, history]
  );

  // Decided once, from the session that existed when the page opened, so a
  // sign-in submitted here still goes through redirectAfterLogin (and the
  // password-manager save) instead of racing an automatic redirect.
  const checkSessionOnMount = useRef(isAuthenticated).current;
  const [sessionCheck, setSessionCheck] = useState(
    checkSessionOnMount ? 'pending' : 'none'
  );

  useEffect(() => {
    if (!checkSessionOnMount) {
      return undefined;
    }

    let settled = false;
    const settle = (result) => {
      if (!settled) {
        settled = true;
        setSessionCheck(result);
      }
    };

    // a hung request falls back to the form rather than a blank page
    const timeout = setTimeout(() => settle('none'), SESSION_CHECK_TIMEOUT_MS);

    // confirm the stored token still works before trusting it; a stale one
    // clears the identity and resolves empty, leaving the form in place.
    // Guests and unverified users stay here so they can sign in as someone else.
    loadMe({ shareInFlight: true })
      .then((freshMe) =>
        settle(
          freshMe && freshMe.verified && !freshMe.is_guest ? 'redirect' : 'none'
        )
      )
      .catch(() => settle('none'));

    return () => {
      settled = true;
      clearTimeout(timeout);
    };
  }, [checkSessionOnMount, loadMe]);

  const _handleLogin = async (form, formEl) => {
    const me = await login(form.username, form.password);
    await storePasswordCredential(
      formEl || {
        id: form.username,
        password: form.password,
      }
    );
    return me;
  };

  const _handleLoginAsGuest = () => {
    return loginAsGuest().then(redirectAfterLogin);
  };

  const { from = {} } = location.state || {};

  const [samlEnabled, setSamlEnabled] = useState(false);
  const checkedDomainsRef = useRef(null);
  if (checkedDomainsRef.current === null) {
    checkedDomainsRef.current = new Map();
  }

  const handleEmailChange = useMemo(() => {
    const discover = debounce(async (email) => {
      const atIndex = email.indexOf('@');
      const domain = atIndex > 0 ? email.substring(atIndex + 1).trim() : '';

      if (domain.length === 0) {
        setSamlEnabled(false);
        return;
      }

      if (checkedDomainsRef.current.has(domain)) {
        setSamlEnabled(checkedDomainsRef.current.get(domain));
      } else {
        try {
          const response = await samlDiscovery(email);
          checkedDomainsRef.current.set(domain, response.enabled);
          setSamlEnabled(response.enabled);
        } catch (error) {}
      }
    }, 300);

    const handler = (email) => {
      const atIndex = email.indexOf('@');
      const domain = atIndex > 0 ? email.substring(atIndex + 1).trim() : '';

      if (domain.length === 0) {
        discover.cancel();
        setSamlEnabled(false);
        return;
      }

      discover(email);
    };

    return handler;
  }, [samlDiscovery]);

  const handleLoginWithSaml = useCallback(
    async (username) => {
      try {
        const response = await samlRedirect(username, fromUrl);
        if (response.redirect_url) {
          window.location.href = response.redirect_url;
        }
      } catch (error) {
        console.error('SAML redirect failed:', error);
      }
    },
    [samlRedirect, fromUrl]
  );

  if (sessionCheck === 'pending') {
    return null;
  }

  if (sessionCheck === 'redirect') {
    return <Redirect to={fromUrl ?? '/dashboard'} />;
  }

  return (
    <LoginPageComponent
      from={from}
      onLogin={_handleLogin}
      onLoginSuccess={redirectAfterLogin}
      onLoginAsGuest={_handleLoginAsGuest}
      onEmailChange={handleEmailChange}
      onLoginWithSaml={handleLoginWithSaml}
      samlEnabled={samlEnabled}
    />
  );
}

export default withRouter(
  connect(
    (state) => ({
      currentWorkspaceId: state.currentWorkspaceId,
      isAuthenticated: selectIsAuthenticated(state),
      me: state.identity.me,
    }),
    {
      login,
      loginAsGuest,
      loadMe,
      samlDiscovery,
      samlRedirect,
    }
  )(LoginPageContainer)
);
